'use client'

import type React from 'react'
import { forwardRef, type HTMLAttributes, type RefAttributes } from 'react'

import {
  type BorderRadiusSize,
  type ThemeBackground,
  themeBackgroundMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

import { type UseSizingProps, useSizing } from '../../hooks/use-sizing.ts'
import { getTestProps, type TestIds } from '../../utils/testing.ts'
import { SurfaceSection } from './surface-section.tsx'
import {
  SurfaceProvider,
  type UseSurfaceProps,
  useSurface,
} from './use-surface.ts'

type SurfaceProps = HTMLAttributes<HTMLElement> &
  RefAttributes<HTMLElement> &
  UseSurfaceProps &
  UseSizingProps &
  TestIds & {
    as?: React.ElementType
    backdropBlur?: boolean
    backgroundColor?: ThemeBackground
    backgroundGlass?: boolean
    border?: boolean
    borderRadius?: BorderRadiusSize
    children?: React.ReactNode
    className?: string
    dropShadow?: boolean
  }

const SurfaceComponent = forwardRef<HTMLElement, SurfaceProps>((props, ref) => {
  const context = useSurface(props)

  return (
    <SurfaceProvider value={context}>
      <SurfaceContent {...props} ref={ref} />
    </SurfaceProvider>
  )
})

const SurfaceContent = forwardRef<HTMLElement, SurfaceProps>((props, ref) => {
  // destructure out all the props that are not valid DOM attributes
  const {
    as: Component = 'section',
    backdropBlur = true,
    backgroundColor,
    backgroundGlass = true,
    border,
    borderRadius = 'lg',
    children,
    className,
    dataTestId,
    dataTrackingId,
    divide,
    dropShadow,
    elevation,
    gap,
    height,
    margin,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    marginX,
    marginY,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingX,
    paddingY,
    size,
    width,
    ...domProps
  } = props
  const context = useSurface(props)

  const {
    gapClassName,
    hasSurfaceChildren,
    marginClassName,
    paddingClassName,
  } = context

  const sizing = useSizing({
    height,
    maxHeight,
    minHeight,
    size,
    width,
    maxWidth,
    minWidth,
  })

  return (
    <SurfaceProvider value={context}>
      <Component
        {...domProps}
        className={cn(
          'flex flex-col scroll-sm rounded-lg',
          backdropBlur && 'backdrop-blur-md',
          backgroundGlass && 'bgg-glass-20',
          backgroundColor && themeBackgroundMap[backgroundColor],
          dropShadow && 'shadow-black-under-20',
          ...Object.values(sizing),
          {
            'grad-border-glass-highlight': border,
            [`${marginClassName}`]: !!marginClassName,
            [`${paddingClassName}`]: !hasSurfaceChildren && !!paddingClassName,
            [`${gapClassName}`]: !!gapClassName,
          },
          className
        )}
        {...getTestProps({ dataTestId, dataTrackingId })}
        ref={ref}
      >
        {children}
      </Component>
    </SurfaceProvider>
  )
})

const SurfaceNamespace = Object.assign(SurfaceComponent, {
  Header: SurfaceSection,
  Section: SurfaceSection,
  Body: SurfaceSection,
  Footer: SurfaceSection,
})

export { SurfaceNamespace as Surface, type SurfaceProps }
